/*
* <license header>
*/

import config from './config.json';


/* global fetch */

/**
 *
 * Invokes a web action
 *
 * @param  {string} actionUrl
 * @param {object} headers
 * @param  {object} params
 *
 * @returns {Promise<string|object>} the response
 *
 */

async function actionWebInvoke (actionUrl, headers = {}, params = {}, options = { method: 'POST' }) {  
  const actionHeaders = {
    'Content-Type': 'application/json',
    ...headers
  }

  const fetchConfig = {
    headers: actionHeaders
  }

  if (window.location.hostname === 'localhost') {
    actionHeaders['x-ow-extra-logging'] = 'on'
  }

  fetchConfig.method = options.method.toUpperCase()

  if (fetchConfig.method === 'GET') {
    actionUrl = new URL(actionUrl)
    Object.keys(params).forEach(key => actionUrl.searchParams.append(key, params[key]))
  } else if (fetchConfig.method === 'POST') {
    fetchConfig.body = JSON.stringify(params)
  }
  
  // console.log('[brand-theme-generator] actionWebInvoke: Calling action URL:', actionUrl);
  // console.log('[brand-theme-generator] actionWebInvoke: Fetch config:', { method: fetchConfig.method, headers: actionHeaders });
  
  const response = await fetch(actionUrl, fetchConfig)

  // console.log('[brand-theme-generator] actionWebInvoke: Response status:', response.status, response.statusText);
  
  let content = await response.text()
  
  if (!response.ok) {
    return JSON.parse(content)
  }
  try {
    content = JSON.parse(content)
  } catch (e) {
    // response is not json
    // console.log('[brand-theme-generator] actionWebInvoke: Response is not JSON, returning as text');
  }
  
  // console.log('[brand-theme-generator] actionWebInvoke: Success response:', content);
  return content
}

/**
 * Fetch current user info from AEM
 * @param {string} aemHost - AEM Host URL
 * @param {object} authConfig - Authentication configuration with imsToken and imsOrg
 * @returns {Promise<object|null>} - User info object or null if failed
 */
async function fetchCurrentUserInfo(aemHost, authConfig) {
  try {
    // console.log('[brand-theme-generator] === fetchCurrentUserInfo: START ===');

    const currentUserInfoEndpoint = config['aem-cf-editor-1/current-user-info'];
    // console.log('[brand-theme-generator] fetchCurrentUserInfo: Endpoint:', currentUserInfoEndpoint);
    
    if (!currentUserInfoEndpoint) {
      throw new Error('Current user info endpoint not configured in config.json');
    }
    
    const headers = {
      'Authorization': `Bearer ${authConfig.imsToken}`
    };
    
    // console.log('[brand-theme-generator] fetchCurrentUserInfo: Invoking action...');

    const currentUserInfoResponse = await actionWebInvoke(
      currentUserInfoEndpoint,
      headers,
      { aemHost: aemHost },
      { method: 'POST' }
    );
    // console.log('[brand-theme-generator] fetchCurrentUserInfo: Response from "aem-cf-editor-1/current-user-info" action:', JSON.stringify(currentUserInfoResponse));
    
    // The action returns: { statusCode: 200, body: { success: true, userId: "user@example.com" } }
    const userId = currentUserInfoResponse?.userId || null;
    // console.log('[brand-theme-generator] fetchCurrentUserInfo: Extracted userId:', userId);
    // console.log('[brand-theme-generator] === fetchCurrentUserInfo: COMPLETED ===');
    return userId;
  } catch (error) {
    console.error('[brand-theme-generator] Error fetching current user info:', error);
    return null;
  }
}

/**
 * @param fragmentPath
 * @param authConfig
 * @param aemHost
 */
async function generateBrandTheme (cfPath, liveSiteURL, authConfig, aemHost) {
  try {

    // console.log('[brand-theme-generator] === generateBrandTheme: START ===');
    // console.log('[brand-theme-generator] generateBrandTheme called with params:', {
    //   cfPath,
    //   liveSiteURL,
    //   authConfig,
    //   aemHost
    // });

    const brandthemeEndpoint = config['aem-cf-editor-1/brand-theme'];
    // console.log('[brand-theme-generator] generateBrandTheme: Endpoint:', brandthemeEndpoint);
    // console.log('[brand-theme-generator] generateBrandTheme: imsToken:', authConfig.imsToken);
    
    if (!brandthemeEndpoint) {
      throw new Error('Brand theme endpoint not configured in config.json');
    }
    
    const headers = {
      'Authorization': `Bearer ${authConfig.imsToken}`,
      'x-gw-ims-org-id': authConfig.imsOrg
    };
    
    // console.log('[brand-theme-generator] generateBrandTheme: Invoking action...');
    const generateThemeResponse = await actionWebInvoke(
      brandthemeEndpoint,
      headers,
      {
        liveSiteURL,
        cfPath,
        authConfig,
        aemHost,
      },
      { method: 'POST' }
    );
    // console.log('[brand-theme-generator] generateBrandTheme: Response from "aem-cf-editor-1/brand-theme" action:', JSON.stringify(generateThemeResponse));
    // console.log('[brand-theme-generator] === generateBrandTheme: COMPLETED ===');

    // Return the response to indicate success
    return generateThemeResponse;

  } catch (e) {
    console.error('[brand-theme-generator] === generateBrandTheme: ERROR ===');
    console.error('[brand-theme-generator] Error type:', e.constructor.name);
    console.error('[brand-theme-generator] Error message:', e.message);
    console.error('[brand-theme-generator] Error stack:', e.stack);
    
    if (e.status) {
      console.error('[brand-theme-generator] HTTP Status:', e.status);
      console.error('[brand-theme-generator] HTTP Status Text:', e.statusText);
    }
    
    if (e.body) {
      console.error('[brand-theme-generator] Error response body:', JSON.stringify(e.body, null, 2));
    }
    
    console.error('[brand-theme-generator] Full error object:', e);
    console.error('[brand-theme-generator] === generateBrandTheme: END ERROR ===');
    
    // Re-throw the error so the calling code can handle it
    throw e;
  }
}

async function getClientIP() {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.error('Failed to get client IP:', error);
    return null;
  }
}

/**
 * Sends analytics data to Adobe Analytics endpoint
 * 
 * @param {Array<{key: string, value: string}>} eVarNEventsData - Array of key-value pairs for eVar and events data
 * @param {string} aemHost - AEM Host URL fetched from guestconnection
 * 
 * @returns {Promise<string|object>} the response
 */
async function sendAnalyticsData(eVarNEventsData = [], aemHost) {
  try {

    const clientIP = await getClientIP();

    // Extract program number and environment number from aemHost
    // Example: author-p159983-e1710854.adobeaemcloud.com
    let programNumber = null;
    let environmentNumber = null;
    
    if (aemHost) {
      const programMatch = aemHost.match(/-p(\d+)/);
      const envMatch = aemHost.match(/-e(\d+)/);
      programNumber = programMatch ? programMatch[1] : null;
      environmentNumber = envMatch ? envMatch[1] : null;
    }

    // Get current user ID from localStorage
    let authorizableId = null;
    
    try {
      // Try to get currentUserId from localStorage first
      const storedUserId = localStorage.getItem('currentUserId');
      if (storedUserId) {
        authorizableId = storedUserId;
        // console.log('[brand-theme-generator] sendAnalyticsData: Retrieved user ID from localStorage:', authorizableId);
      } else {
        console.warn('[brand-theme-generator] sendAnalyticsData: No user ID found in localStorage');
      }
    } catch (storageError) {
      console.warn('[brand-theme-generator] sendAnalyticsData: Error accessing localStorage:', storageError.message);
    }

    // console.log('[brand-theme-generator] === sendAnalyticsData: START ===');
    // console.log('[brand-theme-generator] sendAnalyticsData called with eVarNEventsData:', eVarNEventsData);
    // console.log('[brand-theme-generator] sendAnalyticsData called with aemHost:', aemHost);

    const pageURL = window.location.href;
    // Get user agent
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';

    // Construct eVar elements from key-value pairs with proper indentation
    const eVarElements = eVarNEventsData.map(item => `        <${item.key}>${item.value}</${item.key}>`).join('\n');
    
    // Build optional elements array
    const optionalElements = [];
    if (clientIP) optionalElements.push(`        <ipAddress>${clientIP}</ipAddress>`);
    if (userAgent) optionalElements.push(`        <userAgent>${userAgent}</userAgent>`);
    if (eVarElements) optionalElements.push(eVarElements);
    if (authorizableId) optionalElements.push(`        <eVar3>${authorizableId}</eVar3>`);
    if (programNumber) optionalElements.push(`        <eVar12>${programNumber}</eVar12>`);
    if (environmentNumber) optionalElements.push(`        <eVar13>${environmentNumber}</eVar13>`);
    
    // Construct XML body with proper formatting
    const xmlBody = `<?xml version="1.0" encoding="UTF-8"?>
    <request>
            <pageURL>${pageURL}</pageURL>
            <pageName>BrandThemeGeneratorUsageInfo</pageName>
            <reportSuiteID>aemholreferencedemo</reportSuiteID>
    ${optionalElements.join('\n')}
    </request>`;

    // console.log('[brand-theme-generator] sendAnalyticsData: XML body:', xmlBody);

    const analyticsUrl = 'https://aemhol0.112.2o7.net/b/ss/aemholreferencedemo/6';
    
    const headers = {
      'Content-Type': 'text/xml'
    };

    // console.log('[brand-theme-generator] sendAnalyticsData: Making request to:', analyticsUrl);
    
    const response = await fetch(analyticsUrl, {
      method: 'POST',
      headers: headers,
      body: xmlBody
    });

    // console.log('[brand-theme-generator] sendAnalyticsData: Response status:', response.status, response.statusText);
    
    let content = await response.text();
    
    if (!response.ok) {
      console.error('[brand-theme-generator] sendAnalyticsData: Error response:', content);
      throw new Error(`Analytics request failed: ${response.status} ${response.statusText}`);
    }

    // console.log('[brand-theme-generator] sendAnalyticsData: Success response:', content);
    // console.log('[brand-theme-generator] === sendAnalyticsData: COMPLETED ===');

    return content;

  } catch (e) {
    console.error('[brand-theme-generator] === sendAnalyticsData: ERROR ===');
    console.error('[brand-theme-generator] Error type:', e.constructor.name);
    console.error('[brand-theme-generator] Error message:', e.message);
    console.error('[brand-theme-generator] Error stack:', e.stack);
    console.error('[brand-theme-generator] === sendAnalyticsData: END ERROR ===');
    
    // Re-throw the error so the calling code can handle it
    throw e;
  }
}

module.exports = {
  actionWebInvoke,
  generateBrandTheme,
  sendAnalyticsData,
  fetchCurrentUserInfo,
};